import * as yup from "yup";

import { outcomesSchema } from "../../form/sharedOutcomesSchema";
import {
  createUrlSchema,
  createLatitudeSchema,
  createLongitudeSchema,
  interventionsSchema,
  pillarsSchema,
  commercialInformationSchema,
  narrativeSchema,
  shortDescriptionSchema,
  riskMonitoringSchema,
  solutionFormDependencies,
  urlRegex,
  websiteUrlRegex,
  satelliteImagerySchema,
} from "../../form/sharedSchema";
import { UnitOfSaleOutcome } from "../../form/types";
import { transformTimestampValidity } from "../../formatting/date-time-manipulation";

const editProgrammeInfoSchema = yup.object().shape({
  "asset-type": yup.string().nullable(),

  "description-document": createUrlSchema(
    urlRegex,
    "Must be a valid URL",
    true,
  ),
  "carbon-crediting-programme": yup.array().of(yup.string()),
  "carbon-accounting-methodology": yup.string(),
});

const editInternalDetailsSchema = yup.object().shape({
  "website-url": createUrlSchema(
    websiteUrlRegex,
    "Please paste the Marketing website url starting from 'projects', example: projects/mangroves-maroalika or archived-projects/mangroves-maroalika",
    true,
  ),
  "xero-account-number": yup
    .string()
    .max(10, "Can't be longer than 10 characters"),
  "research-team-notes": yup.string(),
  "additional-resources-link": createUrlSchema(
    urlRegex,
    "Must be a valid URL",
    true,
  ),
});

export const editSolutionFormSchema = yup.object().shape(
  {
    "solution-name": yup.string().required("Project name is required"),

    "solution-assessment-methodology": yup.string().nullable(),
    "solution-intervention-type": yup.string().nullable(),

    "solution-vbc-website-display-name": yup.string().when("outcomes", {
      is: (outcomes: Array<UnitOfSaleOutcome> | undefined) =>
        outcomes?.some(
          (outcome) =>
            outcome.unit === "Biodiversity" && outcome["unit-of-sale"],
        ) ?? false,
      then: (schema) =>
        schema.required(
          "VBC Website Display Name is required when Biodiversity is the unit of sale",
        ),
    }),
    "solution-developer": yup.string(),
    "solution-short-description": shortDescriptionSchema,
    "country-select": yup.array().of(yup.string()).nullable(),
    "solution-development-stage": yup.array().of(yup.string()),
    "solution-is-ready-for-sale": yup.boolean(),
    "solution-not-ready-for-sale-reason": yup
      .string()
      .nullable()
      .when("solution-is-ready-for-sale", {
        is: false,
        then: (schema) =>
          schema.required("Not ready for sale reason must be provided"),
        otherwise: (schema) => schema.strip(),
      }),
    "solution-sdg": yup.array().of(yup.number()),
    "location-pin-map-link": createUrlSchema(
      urlRegex,
      "Must be a valid URL",
      true,
    ),
    "solution-start-date": yup
      .boolean()
      .transform(transformTimestampValidity)
      .isFalse("Must be a valid date in dd/mm/yyyy"),
    "solution-end-date": yup
      .boolean()
      .transform(transformTimestampValidity)
      .isFalse("Must be a valid date in dd/mm/yyyy"),
    latitude: createLatitudeSchema(true),
    longitude: createLongitudeSchema(true),
    "solution-is-carbon-offset": yup.boolean(),
    interventions: interventionsSchema,
    outcomes: outcomesSchema,
    pillars: pillarsSchema,
    "programme-info": editProgrammeInfoSchema,
    "internal-details": editInternalDetailsSchema,
    "commercial-information": commercialInformationSchema,
    narrative: narrativeSchema,
    "satellite-imagery": satelliteImagerySchema,
    "risk-monitoring": riskMonitoringSchema,
  },
  solutionFormDependencies,
);
